import type { EmployerDataFilter } from 'app/types';

import type { UserAccessesListSelectedData } from './UserAccessesList.types';

export const isEmployerSelected = (
  selectedData: UserAccessesListSelectedData | undefined,
  employerId: string,
): boolean => {
  return !!selectedData?.allEmployers || !!selectedData?.employerIds.includes(employerId);
};

export const getEmployerWithSubEmployerIds = (employer: EmployerDataFilter): string[] => {
  return [employer.employerId, ...employer.subEmployers.flatMap(getEmployerWithSubEmployerIds)];
};

export const getEmployerIds = (employers: Array<EmployerDataFilter>): string[] =>
  employers.flatMap(getEmployerWithSubEmployerIds);

export const areEmployerIdsSelected = (
  selectedData: UserAccessesListSelectedData | undefined,
  employerIds: Array<string>,
): boolean =>
  !!selectedData?.allEmployers ||
  (employerIds.length > 0 && employerIds.every(employerId => selectedData?.employerIds.includes(employerId)));

export const toggleEmployerIds = (
  currentSelectedData: UserAccessesListSelectedData,
  employerIds: Array<string>,
): UserAccessesListSelectedData => {
  if (currentSelectedData.allEmployers) return currentSelectedData;

  const uniqueEmployerIds = Array.from(new Set(employerIds));
  const allSelected = areEmployerIdsSelected(currentSelectedData, uniqueEmployerIds);

  return {
    ...currentSelectedData,
    employerIds: allSelected
      ? currentSelectedData.employerIds.filter(id => !uniqueEmployerIds.includes(id))
      : Array.from(new Set([...currentSelectedData.employerIds, ...uniqueEmployerIds])),
    eventFlows: allSelected
      ? removeEventFlows(currentSelectedData.eventFlows || [], uniqueEmployerIds)
      : currentSelectedData.eventFlows || [],
  };
};

const removeEventFlows = (eventFlows: string[], ids: string[]): string[] => eventFlows.filter(id => !ids.includes(id));

export const toggleEmployerSelection = (
  currentSelectedData: UserAccessesListSelectedData,
  employer: EmployerDataFilter,
): UserAccessesListSelectedData => {
  return toggleEmployerIds(currentSelectedData, getEmployerWithSubEmployerIds(employer));
};

export const toggleSubEmployerSelection = (
  currentSelectedData: UserAccessesListSelectedData,
  employerId: string,
  subEmployerId: string,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isEmployerSelected(currentSelectedData, subEmployerId);

  return {
    ...currentSelectedData,
    allEmployers: false,
    employerIds: isCurrentlySelected
      ? currentSelectedData.employerIds.filter(id => id !== subEmployerId)
      : Array.from(new Set([...currentSelectedData.employerIds, employerId, subEmployerId])),
    eventFlows: isCurrentlySelected
      ? removeEventFlows(currentSelectedData.eventFlows || [], [subEmployerId])
      : currentSelectedData.eventFlows || [],
  };
};

export const toggleAllEmployers = (
  currentSelectedData: UserAccessesListSelectedData,
): UserAccessesListSelectedData => ({
  ...currentSelectedData,
  allEmployers: !currentSelectedData.allEmployers,
  employerIds: [],
});
